import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaTrash, FaUser, FaCheck } from 'react-icons/fa';
import './ChatRoomList.scss';
import fnc from '../../mymod/commFunction';
import { normalizeAcaCode, normalizeChatUserId } from '../../mymod/normalizeAcaCode';

const ChatRoomList = ({ 
  chatrooms, 
  currentChatroom, 
  onSelectChatroom, 
  onCreateChatroom,
  onDeleteChatroom,
  currentUserId,
  currentUserIdCandidates = []
}) => {

  const normalizeDisplayId = (rawId) => {
    if (typeof rawId !== 'string') {
      return '';
    }

    const trimmed = rawId.trim();
    if (!trimmed) {
      return '';
    }

    const tokens = trimmed.split('_');
    if (tokens.length >= 2 && /^(?:[A-Za-z]+\d+|\d+)$/.test(tokens[0])) {
      return tokens.slice(1).join('_');
    }

    return trimmed;
  };

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [participantNameMap, setParticipantNameMap] = useState({});

  const userInfo = useSelector((state) => state.reduxStore.userInfo);
  const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
  const currentAcaCode = normalizeAcaCode(
    userInfo2?.acaCode
    || userInfo?.acaCode
    || fnc.getCookie('acaCode')
    || fnc.getCookie('certCode')
  );
  const currentUserIds = [...new Set([
    currentUserId,
    ...currentUserIdCandidates
  ].filter(Boolean))];

  // 사용자 목록 조회
  useEffect(() => {
    if (showCreateForm) {
      loadUsers();
    }
  }, [showCreateForm]);

  useEffect(() => {
    fnc.sendAxios('/socialLoginRoutes/select/allUsers2', { acaCode: currentAcaCode }, (res) => {
      if (!Array.isArray(res)) {
        setParticipantNameMap({});
        return;
      }

      const nameMap = {};
      res.forEach((user) => {
        if (!user?.id) {
          return;
        }

        const normalizedUserId = normalizeChatUserId(user.id, currentAcaCode);
        const displayName = user.nickname || user.name || user.id2 || normalizeDisplayId(user.id);
        nameMap[user.id] = displayName;
        nameMap[normalizedUserId] = displayName;
      });

      setParticipantNameMap(nameMap);
    });
  }, [currentAcaCode]);

  // const loadUsers = () => {
  //   fnc.sendAxios('/socialLoginRoutes/select/allUsers', {}, (res) => {
  //     if (res && Array.isArray(res)) {
  //       // 현재 사용자 제외
  //       const otherUsers = res.filter(user => user.id !== currentUserId);
  //       setUsers(otherUsers);
  //     }
  //   });
  // };


    // 사용자 목록 로드
    const loadUsers = () => {
      fnc.sendAxios('/socialLoginRoutes/select/allUsers2', { acaCode: currentAcaCode }, (res) => {
        if (res && Array.isArray(res)) {
          // 이미 채팅방에 참여 중인 사용자 제외
          // const currentParticipants = chatroom?.participants || [];
          // const availableUsers = res.filter(user => !currentParticipants.includes(user.id));
          // setUsers(availableUsers);
          const normalizedUsers = res.map((user) => ({
            ...user,
            acaCode: normalizeAcaCode(user.acaCode || currentAcaCode),
            id: normalizeChatUserId(user.id, user.acaCode || currentAcaCode)
          }));
          const otherUsers = normalizedUsers.filter(user => user.id !== currentUserId);
        setUsers(otherUsers);
        }
      });
    };

  // 사용자 선택/해제
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // 검색 필터링
  const filteredUsers = users.filter(user => 
    !searchKeyword || 
    user.nickname?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    user.id?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 새 채팅방 생성 핸들러
  const handleCreateRoom = (e) => {
    e.preventDefault();
    if (newRoomName.trim()) {
      onCreateChatroom(newRoomName.trim(), selectedUsers);
      setNewRoomName('');
      setSelectedUsers([]);
      setSearchKeyword('');
      setShowCreateForm(false);
    }
  };

  // 마지막 메시지 시간 포맷
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? '방금' : `${diffInMinutes}분 전`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}시간 전`;
    } else {
      return messageDate.toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // 읽지 않은 메시지 수 가져오기
  const getUnreadCount = (chatroom) => {
    const unreadCount = chatroom?.unreadCount || {};
    return Math.max(
      0,
      ...currentUserIds.map((candidateId) => Number(unreadCount[candidateId] || 0))
    );
  };

  const getParticipantNames = (chatroom) => {
    const participants = chatroom?.participants || [];

    if (!participants.length) {
      return '';
    }

    return participants
      .filter((participantId) => !currentUserIds.includes(participantId))
      .map((participantId) => participantNameMap[participantId] || normalizeDisplayId(participantId))
      .join(', ');
  };

  return (
    <div className="chatroom-list">
      <div className="chatroom-list-header">
        <h3>채팅방</h3>
        <button 
          className="create-room-btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
          title="새 채팅방 만들기"
        >
          +
        </button>
      </div>

      {/* 새 채팅방 생성 폼 */}
      {showCreateForm && (
        <div className="create-room-form">
          <form onSubmit={handleCreateRoom}>
            <input
              type="text"
              placeholder="채팅방 이름을 입력하세요"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              autoFocus
              required
            />

            {/* 참여자 선택 */}
            {/* <div className="participants-section">
              <label>참여자 선택 ({selectedUsers.length}명 선택됨)</label>
              
              <input
                type="text"
                placeholder="사용자 검색..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="search-input"
              />

              <div className="users-list">
                {filteredUsers.length === 0 ? (
                  <p className="no-users">사용자가 없습니다.</p>
                ) : (
                  filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className={`user-item ${selectedUsers.includes(user.id) ? 'selected' : ''}`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="user-avatar">
                        {user.profile_image ? (
                          <img src={user.profile_image} alt={user.nickname} />
                        ) : (
                          <span>{user.nickname?.charAt(0)?.toUpperCase() || '?'}</span>
                        )}
                      </div>
                      <div className="user-info">
                        <span className="user-nickname">{user.nickname || '사용자'}</span>
                        <span className="user-id">{user.id2 || user.id}</span>
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <FaCheck className="check-icon" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div> */}

            <div className="form-buttons">
              <button 
                type="submit" 
                className="create-btn"
                disabled={!newRoomName.trim()}
              >
                생성
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewRoomName('');
                  setSelectedUsers([]);
                  setSearchKeyword('');
                }}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 채팅방 목록 */}
      <div className="chatroom-items">
        {chatrooms.length === 0 ? (
          <div className="no-chatrooms">
            <p>채팅방이 없습니다.</p>
            <button onClick={() => setShowCreateForm(true)}>
              새 채팅방 만들기
            </button>
          </div>
        ) : (
          chatrooms.map(chatroom => (
            <div
              key={chatroom.id}
              className={`chatroom-item ${
                currentChatroom?.id === chatroom.id ? 'active' : ''
              }`}
              onClick={() => onSelectChatroom(chatroom)}
            >
              <div className="chatroom-avatar">
                <div className="avatar-circle">
                  {chatroom.name.charAt(0).toUpperCase()}
                </div>
                {chatroom.isOnline && (
                  <div className="online-indicator"></div>
                )}
              </div>
              
              <div className="chatroom-info">
                <div className="chatroom-header">
                  <h4 className="chatroom-name">{chatroom.name}</h4>
                  <div className="participants-count">
                    <FaUser size={10} />
                    <span className="participant-total">{chatroom.participants?.length || 0}</span>
                    <span className="participant-names">
                      {getParticipantNames(chatroom) || '참여자 없음'}
                    </span>
                  </div>
                  <span className="last-message-time">
                    {formatLastMessageTime(chatroom.lastMessageTime)}
                  </span>
                </div>
                
                <div className="chatroom-footer">
                  <p className="last-message">
                    {chatroom.lastMessage || '메시지가 없습니다.'}
                  </p>
                  {getUnreadCount(chatroom) > 0 && (
                    <span className="unread-badge">
                      {getUnreadCount(chatroom)}
                    </span>
                  )}
                </div>
              </div>
              
              <button
                className="delete-chatroom-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`"${chatroom.name}" 채팅방을 삭제하시겠습니까?`)) {
                    onDeleteChatroom(chatroom.id);
                  }
                }}
                title="채팅방 삭제"
              >
                <FaTrash size={12} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ChatRoomList;
